import {useMutation} from '@apollo/react-hooks';

import type {
    GetLeadChannels as GetLeadChannelsQuery,
    GetSources as GetSourcesQuery,
    LeadAddSource as LeadAddSourceMutation,
    LeadAddSourceVariables as LeadAddSourceMutationVariables,
    LeadAddSourceInput,
} from 'nutshell-graphql-types';

import GET_SOURCES from '../../../setup/sources/graphql/queries/get-sources.graphql';
import {SOURCES_WRAPPER_LIMIT} from '../../../setup/sources/helpers';

import LEAD_ADD_SOURCE from '../graphql/mutations/lead-add-source.graphql';
import GET_LEAD_CHANNELS from '../graphql/queries/get-lead-channels.graphql';

export function useAddSource(): (arg1: LeadAddSourceInput) => Promise<any> {
    const [addSource] = useMutation<LeadAddSourceMutation, LeadAddSourceMutationVariables>(
        LEAD_ADD_SOURCE
    );

    return (input: LeadAddSourceInput) => {
        return addSource({
            variables: {input},
            optimisticResponse: {
                leadAddSource: {
                    __typename: 'LeadAddSourcePayload',
                    lead: {
                        __typename: 'Lead',
                        id: input.leadId,
                    },
                },
            },
            update: (proxy) => {
                // Need to check the cached results from the sources query first
                const sourcesCacheResult: GetSourcesQuery | null | undefined = proxy.readQuery({
                    query: GET_SOURCES,
                    // Needs to exactly match query params used in useGetSources() in SourcesSectionWrapper
                    variables: {sort: 'NAME_ASC', first: SOURCES_WRAPPER_LIMIT},
                });

                if (sourcesCacheResult && sourcesCacheResult.sources) {
                    // Find the added source's info from the cache using its id
                    const addedSource = sourcesCacheResult.sources.edges.find(
                        (source) => source.node.id === input.sourceId
                    );

                    if (addedSource) {
                        // Now check the cached lead channels query
                        const leadChannelCacheResult: GetLeadChannelsQuery | null | undefined =
                            proxy.readQuery({
                                query: GET_LEAD_CHANNELS,
                                variables: {id: input.leadId},
                            });

                        if (leadChannelCacheResult && leadChannelCacheResult.lead) {
                            // Defined at upper level for singular proxy write at end of block
                            const leadSources = leadChannelCacheResult.lead.channels
                                ? leadChannelCacheResult.lead.channels.sources
                                : [];

                            const leadChannels = leadChannelCacheResult.lead.channels
                                ? leadChannelCacheResult.lead.channels.edges
                                : [];

                            // If the added source has a channel
                            if (addedSource.node.channel) {
                                // Check if the lead has channels added
                                if (leadChannels.length) {
                                    // Check if the new source's channel exists already
                                    const channelIndex = leadChannels.findIndex(
                                        (channel) =>
                                            // @ts-expect-error
                                            channel.node.value === addedSource.node.channel.value
                                    );

                                    // If the new source's channel exists on the lead
                                    if (channelIndex !== -1) {
                                        // Add the new source to the existing list of channel sources
                                        // @ts-expect-error
                                        leadChannels[channelIndex].sources.push({
                                            ...addedSource.node,
                                            __typename: 'Source',
                                        });

                                        // Needs to be sorted alphabetically - so resort
                                        // @ts-expect-error
                                        leadChannels[channelIndex].sources.sort((a, b) =>
                                            a.name.localeCompare(b.name)
                                        );
                                    } else {
                                        // The channel needs to be added to the lead with the new source
                                        leadChannels.push({
                                            __typename: 'LeadChannelsEdge',
                                            node: {
                                                __typename: 'Channel',
                                                value: addedSource.node.channel.value,
                                                title: addedSource.node.channel.title,
                                            },
                                            sources: [
                                                {
                                                    ...addedSource.node,
                                                    __typename: 'Source',
                                                },
                                            ],
                                        });
                                    }
                                } else {
                                    // Lead has no channels, so add this new one + the source
                                    leadChannels.push({
                                        __typename: 'LeadChannelsEdge',
                                        node: {
                                            __typename: 'Channel',
                                            value: addedSource.node.channel.value,
                                            title: addedSource.node.channel.title,
                                        },
                                        sources: [
                                            {
                                                ...addedSource.node,
                                                __typename: 'Source',
                                            },
                                        ],
                                    });
                                }
                            } else {
                                // The new source doesn't have a channel, so we just add it to the sources array
                                leadSources.push({
                                    ...addedSource.node,
                                    __typename: 'Source',
                                    channel: null,
                                });

                                // The sources array needs to be in alphabetical order
                                leadSources.sort((a, b) => a.name.localeCompare(b.name));
                            }

                            // Update the cache with new data
                            proxy.writeQuery({
                                query: GET_LEAD_CHANNELS,
                                variables: input.leadId,
                                data: {
                                    lead: {
                                        ...leadChannelCacheResult.lead,
                                        channels: {
                                            ...leadChannelCacheResult.lead.channels,
                                            edges: [...leadChannels],
                                            sources: leadSources,
                                        },
                                    },
                                },
                            });
                        }
                    }
                }
            },
        });
    };
}
